/**
 * Credit note (design J, 620 px). The value shown while choosing lines is a
 * preview (line total × quantity ÷ line quantity, half up); the server works
 * out the credit note's own figures, and the page reloads them afterwards.
 */
import React, { useState } from 'react';
import InvoiceRepository, { CreditNoteInput, InvoiceDoc } from '../../../internals/repository/InvoiceRepository';
import { money, parseMoney, qty } from '../format';
import { creditLineValue, errorText, validCreditQty } from '../detailModel';
import { Dialog, ErrorBanner } from './VoidModal';

export const CREDIT_REASONS = [
  { code: 'RETURNED', label: 'Returned or not delivered' },
  { code: 'FEWER_UNITS', label: 'Fewer guests than invoiced' },
  { code: 'PRICE_CORRECTION', label: 'Price correction' },
  { code: 'GOODWILL', label: 'Goodwill discount' },
  { code: 'OTHER', label: 'Other' },
] as const;

type Mode = 'lines' | 'amount';
const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';

export default function CreditNoteModal({
  shopId, doc, onClose, onDone,
}: {
  shopId: string;
  doc: InvoiceDoc;
  onClose: () => void;
  onDone: (res: { creditNote: InvoiceDoc; document: InvoiceDoc }) => void;
}) {
  const cur = doc.currency;
  const balance = Math.max(0, doc.balanceMinor);
  const [mode, setMode] = useState<Mode>('lines');
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState<string>('RETURNED');
  const [otherText, setOtherText] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lineRows = doc.lines.map((l) => {
    const text = qtys[l.lineId] ?? '';
    const ok = validCreditQty(text, l.quantity);
    const q = ok && text.trim() ? Number(text) : 0;
    return { l, text, ok, q, value: ok ? creditLineValue(l, q) : 0 };
  });
  const badQty = lineRows.some((r) => !r.ok);
  const amountMinor = parseMoney(amount);
  const value = mode === 'lines'
    ? lineRows.reduce((a, r) => a + r.value, 0)
    : amountMinor > 0 ? amountMinor : 0;
  const over = value > balance;
  const reasonLabel = CREDIT_REASONS.find((r) => r.code === reasonCode)?.label ?? 'Other';
  const reasonText = reasonCode === 'OTHER' ? otherText.trim() : reasonLabel;
  const otherMissing = reasonCode === 'OTHER' && !otherText.trim();
  const amountBad = mode === 'amount' && amount.trim() !== '' && !(amountMinor > 0);
  const disabled = busy || !(value > 0) || over || badQty || amountBad;

  const submit = async () => {
    setTouched(true);
    if (disabled || otherMissing) return;
    setBusy(true);
    setError(null);
    const input: CreditNoteInput = mode === 'lines'
      ? { reason: reasonText, reasonCode, lines: lineRows.filter((r) => r.q > 0).map((r) => ({ lineId: r.l.lineId, quantity: r.q })) }
      : { reason: reasonText, reasonCode, amountMinor };
    try {
      onDone(await InvoiceRepository.creditNote(shopId, doc.docId, input));
    } catch (e: any) {
      setError(errorText(e, {
        amount_exceeds_balance: 'The credit is more than the balance due.',
        invalid_state: 'A credit note can only be issued against an open invoice. Reload to see its current state.',
      }));
      setBusy(false);
    }
  };

  return (
    <Dialog
      width={620}
      title={`Credit note for ${doc.number ?? 'this invoice'}`}
      sub="Reduces what the customer owes. The credit note gets its own number and PDF."
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={submit} disabled={disabled}>
            {busy ? 'Issuing…' : 'Issue credit note'}
          </button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div role="radiogroup" aria-label="How to credit" className="flex gap-0.5 self-start rounded-lg border border-borderc bg-surface2 p-[3px]">
          {([['lines', 'Choose lines'], ['amount', 'Enter an amount']] as [Mode, string][]).map(([k, label]) => {
            const on = mode === k;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setMode(k)}
                className="rounded-md border-0 px-3 py-1.5 text-[13px] font-semibold"
                style={{
                  background: on ? 'var(--surface)' : 'transparent',
                  color: on ? 'var(--text)' : 'var(--text2)',
                  boxShadow: on ? '0 1px 2px rgba(0,0,0,.12)' : 'none',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {mode === 'lines' ? (
          <div className="overflow-x-auto rounded-xl border border-borderc">
            <div style={{ minWidth: 480 }}>
              <div
                className="grid gap-3 bg-surface2 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[.07em] text-text3"
                style={{ gridTemplateColumns: 'minmax(0,1fr) 110px 120px' }}
              >
                <span>Line</span><span className="text-right">Qty to credit</span><span className="text-right">Value</span>
              </div>
              {lineRows.map(({ l, text, ok, value: v }) => (
                <div
                  key={l.lineId}
                  className="grid items-center gap-3 border-t border-borderc px-3.5 py-2.5 text-[13px]"
                  style={{ gridTemplateColumns: 'minmax(0,1fr) 110px 120px' }}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-text1">{l.description}</span>
                    <span className="text-[12px] text-text3">
                      of {qty(l.quantity)}{l.unit ? ` ${l.unit}` : ''} at {money(l.unitPriceMinor, cur)}
                    </span>
                  </span>
                  <span className="flex flex-col items-end">
                    <input
                      className="nl-input nl-input--sm nl-num text-right"
                      inputMode="decimal"
                      aria-label={`Quantity to credit for ${l.description}`}
                      aria-invalid={!ok}
                      placeholder="0"
                      value={text}
                      style={!ok ? { borderColor: 'var(--neg)', width: 96 } : { width: 96 }}
                      onChange={(e) => setQtys((s) => ({ ...s, [l.lineId]: e.target.value }))}
                    />
                    {!ok && <span className="text-[11px]" style={{ color: 'var(--negFg)' }}>0 to {qty(l.quantity)}</span>}
                  </span>
                  <span className="nl-num text-right tabular-nums text-text1">{money(v, cur)}</span>
                </div>
              ))}
            </div>
            <p className="m-0 border-t border-borderc px-3.5 py-2 text-[12px] text-text3">
              Values are an estimate. The credit note's final figure, including VAT, is worked out when it is issued.
            </p>
          </div>
        ) : (
          <label className={labelCls}>
            Amount including VAT
            <input
              className="nl-input nl-num"
              inputMode="decimal"
              value={amount}
              placeholder="0.00"
              aria-invalid={amountBad}
              style={amountBad ? { borderColor: 'var(--neg)' } : undefined}
              onChange={(e) => setAmount(e.target.value)}
            />
            {amountBad && <span className="text-[12px] font-medium" style={{ color: 'var(--negFg)' }}>! Enter an amount.</span>}
          </label>
        )}

        <label className={labelCls}>
          Reason
          <select className="nl-select" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
            {CREDIT_REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        {reasonCode === 'OTHER' && (
          <label className={labelCls}>
            Describe the reason
            <input
              className="nl-input"
              value={otherText}
              maxLength={280}
              placeholder="Short reason, shown on the credit note"
              aria-invalid={touched && otherMissing}
              style={touched && otherMissing ? { borderColor: 'var(--neg)' } : undefined}
              onChange={(e) => setOtherText(e.target.value)}
            />
            {touched && otherMissing && <span className="text-[12px] font-medium" style={{ color: 'var(--negFg)' }}>! Enter a reason.</span>}
          </label>
        )}

        <div className="grid grid-cols-3 gap-3 rounded-[10px] bg-surface2 px-4 py-3 text-[14px]">
          <div className="flex flex-col gap-0.5"><span className="text-[12px] text-text3">Balance now</span><span className="nl-num font-semibold text-text1">{money(balance, cur)}</span></div>
          <div className="flex flex-col gap-0.5"><span className="text-[12px] text-text3">Credit</span><span className="nl-num font-semibold" style={{ color: 'var(--infoFg)' }}>− {money(value, cur)}</span></div>
          <div className="flex flex-col gap-0.5"><span className="text-[12px] text-text3">New balance</span><span className="nl-num font-bold text-text1">{money(Math.max(0, balance - value), cur)}</span></div>
        </div>
        {over && <span role="alert" className="text-[12px] font-semibold" style={{ color: 'var(--negFg)' }}>! The credit is more than the balance due.</span>}
        <ErrorBanner text={error} />
      </div>
    </Dialog>
  );
}
